/* eslint-disable @typescript-eslint/no-explicit-any */
import { HandlerEvent } from 'post-robot';
import { error, ValidationError } from 'common/errors';
import { asyncMarkWrapper, markEnd } from 'common/instrumentation';
import {
  getState,
  CommunicatorState,
  saveApiResponseCache,
  saveMeta,
  savePreferredNetwork
} from 'orch-communicator/store';
//START GENAI@GHCOPILOT
import { getSupportedCardNetworks } from 'orch-communicator/store/selectors';
//END GENAI@GHCOPILOT
import { CardNetwork } from 'types/data';
import { IdentityLookupPayload, IdLookupData } from 'types/vsb-sdk/identity-lookup';

//START GENAI@GHCOPILOT

type PerformIdentityLookupResponse = {
  network: CardNetwork;
  result: IdentityLookupPayload | ValidationError;
};
type PerformIdentityLookupInput = {
  consumerIdentity: IdLookupData;
  network: CardNetwork;
  identityLookUpResponses: { [key: string]: IdentityLookupPayload | ValidationError | null };
};

type Accumulator = {
  mostRecentResponse: PromiseFulfilledResult<PerformIdentityLookupResponse> | null;
  rejectedResponses: PromiseRejectedResult[];
};

async function performIdentityLookup(
  input: PerformIdentityLookupInput
): Promise<PerformIdentityLookupResponse> {
  //START GENAI@GHCOPILOT
  const { consumerIdentity, network, identityLookUpResponses } = input;

  try {
    const vSDK = getState().srcSdks.loadedSdk as { [key: string]: any };
    /* START GENAI@GHCOPILOT */
    const result = await vSDK[network].identityLookup(consumerIdentity);
    identityLookUpResponses[network] = result;
    return { result, network };
    //START GENAI@GHCOPILOT
  } catch (e: any) {
    markEnd(`vsb_idlookup_${network}`, { failure: true });
    /* END GENAI@GHCOPILOT */
    if (network === 'VISA') {
      //eslint-disable-next-line
      e = e.error;
    }

    if (e && 'reason' in e && e.reason === 'ACCT_INACCESSIBLE') {
      const errorMessage = 'The user account exists but is not currently accessible.';
      const inaccessibleError = error('ACCT_INACCESSIBLE', errorMessage, ' ' as any);
      identityLookUpResponses[network] = inaccessibleError;
      throw inaccessibleError;
    }

    const serverError = error('SERVER_ERROR', 'General Server Error.', ' ' as any);
    identityLookUpResponses[network] = serverError;
    throw serverError;

    //END GENAI@GHCOPILOT
  }
  //END GENAI@GHCOPILOT
}

export default async function identityLookup(
  event: HandlerEvent<IdLookupData>
): Promise<IdentityLookupPayload | ValidationError | null> {
  const identityLookUpResponses: { [key: string]: IdentityLookupPayload | ValidationError | null } =
    {};
  //START GENAI@CHATGPT4

  const cardNetworks = getSupportedCardNetworks() as CardNetwork[];
  //START GENAI@GHCOPILOT

  const fulfiledNetworks: string[] = [];

  const consumerIdentity = event.data;

  let fastestNetwork: CardNetwork | '' = '';

  const promises = cardNetworks.map(network => {
    return asyncMarkWrapper(
      'idLookup',
      network,
      performIdentityLookup({ consumerIdentity, network, identityLookUpResponses })
    );
  });

  //START GENAI@CHATGPT4

  //START GENAI@CHATGPT4
  const isIdentityLookupPayload = (
    result: IdentityLookupPayload | ValidationError
  ): result is IdentityLookupPayload => {
    return (result as IdentityLookupPayload).consumerPresent === true;
  };

  try {
    const allResponses = await Promise.allSettled(promises);
    //START GENAI@GHCOPILOT
    //eslint-disable-next-line
    const { mostRecentResponse, rejectedResponses } = allResponses.reduce(
      (accumulator: Accumulator, currentResponse) => {
        const isCurrentResponseFulfilled = currentResponse.status === 'fulfilled';
        //START GENAI@GHCOPILOT

        if (isCurrentResponseFulfilled) {
          fulfiledNetworks.push(currentResponse.value.network);
        }

        const isCurrentResponseValid =
          isCurrentResponseFulfilled && isIdentityLookupPayload(currentResponse.value.result);

        const isCurrentResponseMoreRecent =
          isCurrentResponseFulfilled &&
          isIdentityLookupPayload(currentResponse.value.result) &&
          (!accumulator.mostRecentResponse ||
            (isIdentityLookupPayload(accumulator.mostRecentResponse.value.result) &&
              accumulator.mostRecentResponse.value.result.consumerPresent === true &&
              // eslint-disable-next-line
              currentResponse.value.result.lastUsedCardTimestamp! >
                // eslint-disable-next-line
                accumulator.mostRecentResponse.value.result.lastUsedCardTimestamp!));

        const isMostRecentResponseNotSet = !accumulator.mostRecentResponse;

        if (isCurrentResponseMoreRecent || (isCurrentResponseValid && isMostRecentResponseNotSet)) {
          accumulator.mostRecentResponse = currentResponse;
        } else if (currentResponse.status === 'rejected') {
          accumulator.rejectedResponses.push(currentResponse);
        }

        return accumulator;
      },
      { mostRecentResponse: null, rejectedResponses: [] }
    );
    //END GENAI@GHCOPILOT
    //END GENAI@CHATGPT4

    if (mostRecentResponse) {
      const { network, result } = mostRecentResponse.value;
      fastestNetwork = network;
      return result;
    }

    const unfulfilledNetworks = cardNetworks.filter(network => !fulfiledNetworks.includes(network));

    if (unfulfilledNetworks.length > 0) {
      if (unfulfilledNetworks.includes('VISA')) {
        /* eslint-disable */
        if (identityLookUpResponses['VISA'] && 'error' in identityLookUpResponses['VISA'])
          throw identityLookUpResponses['VISA'];
        else throw error('SERVER_ERROR', 'General Server Error.', ' ' as any);
      } else {
        if (identityLookUpResponses[unfulfilledNetworks[0]])
          throw identityLookUpResponses[unfulfilledNetworks[0]];
        throw error('SERVER_ERROR', 'General Server Error.', ' ' as any);
      }
    }

    return identityLookUpResponses['VISA'];
    /* eslint-enable */
    //END GENAI@GHCOPILOT
    //END GENAI@CHATGPT4
  } catch (e) {
    throw e;
  } finally {
    const communicatorState = getState();
    const apiResponseCache = {
      ...communicatorState.srcSdks.apiResponseCache,
      identityLookUp: {
        ...communicatorState.srcSdks.apiResponseCache.identityLookUp,
        ...identityLookUpResponses
      }
    };
    //END GENAI@CHATGPT4

    saveApiResponseCache(apiResponseCache);

    const metaUpdates: Partial<CommunicatorState['meta']> = {
      otpProvider: fastestNetwork
    };

    saveMeta(metaUpdates);
    savePreferredNetwork(fastestNetwork);
  }
  //END GENAI@GHCOPILOT
}
